// ./src/components/layouts/withPlanGate.tsx
//
// ⭐ TURNS A SITEMAP BINDING INTO A WHOLE-PAGE PLAN GATE.
//
// `siteMap.ts` declares `featureCode` on the pages a feature owns; this wraps those pages so a
// host whose plan lacks the feature gets the upgrade screen INSTEAD of the page — rather than a
// working page whose Add button refuses them at the end.
//
// ⚠ WHY THE PAGE IS NOT MOUNTED AT ALL WHEN LOCKED. The alternative (render the page, put a notice
// on top) means the host reads a screen they cannot use, and every list query, provider and
// drawer behind it runs to fetch nothing. Not mounting it is both the honest answer and the cheap
// one.
//
// ⚠ THE LOCK KEEPS THE CHROME — a locked page still has the sidebar, the tab bar and the header. A
// bare upgrade screen would be a dead end: the host would have to use the browser's Back button to
// escape a page they navigated to on purpose.
// ⓘ Since ROADMAP A2b #58 it gets that for free. The shell is rendered ABOVE the route
// (`AppShellLayout`), so this wrapper is already INSIDE it and simply renders the upgrade screen;
// it used to have to render `DashboardLayout` itself. The header is left empty on purpose — the
// page never mounts, so nothing publishes chrome, and offering that page's actions over a lock
// screen would advertise things the plan does not allow.
//
// ⚠ IT DOES NOT DECIDE ANYTHING ITSELF. `useFeatureGate().checkFeature` is the one resolver, and
// `PremiumFeatureGate` owns the screen; this only chooses between them and supplies the layout.
// The kill switch and `?paywalls=preview` are handled inside `checkFeature`, so a locked page is
// invisible until paywalls are previewed or switched on.
import React, { Suspense, useEffect } from "react";
import { useParams } from "react-router-dom";

import { useApp } from "@/contexts/AppContext";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { useFeatureUsage } from "@/hooks/useFeatureUsage";

import { FeatureGateContent } from "@/components/generic/FeatureGateContent";
import { PremiumFeatureGate } from "@/components/generic/PremiumFeatureGate";
import { useCrewAccess } from "@/contexts/CrewAccessContext/CrewAccessContext";

/**
 * Wrap a page component so it is replaced by the upgrade screen when the plan lacks `featureCode`.
 *
 * @param Component   the page (usually a `React.lazy` one from the sitemap)
 * @param featureCode the feature the page belongs to — declared in `siteMap.ts`, never here
 * @param gateKey     which per-page block of `plans.json` the lock screen reads its copy from
 * @param options     `currentCount` for a NUMERIC feature.
 *
 * ⚠ `currentCount` is for a page wrapping ITSELF, never for a `siteMap.ts` binding. A route cannot
 * know a count, and the entitlement rules answer "allowed" without one — so a numeric feature bound
 * in the sitemap gates nothing, silently. `check-entitlement` refuses that; this parameter is the
 * supported way for a page that DOES know its count (UsersPage: "the owner holds seat 1") to get
 * the same layout-preserving lock.
 */
/**
 * ⚠⚠ MEMOISED PER (component, feature, key, count) — AND THIS IS A CORRECTNESS FIX, NOT A
 * MICRO-OPTIMISATION.
 *
 * A sitemap module registers the SAME page component on several routes: `/registration`,
 * `/registration/:id/edit`, `/registration/:id/rules`, `/registration/:id/grants`. React keeps a
 * component mounted across a route change only while the element TYPE is identical. Wrapping each
 * entry separately produced a different `Gated` function per route, so opening a URL-stage drawer
 * unmounted and remounted the whole page — every list refetched, every bit of local state lost.
 *
 * It showed up as "the tickets reload whenever a drawer opens, and it doesn't happen on other
 * pages": ticketing is the only bound module a free account can actually open, so it was the only
 * place the remount was visible. Caching the wrapper restores one identity per page.
 */
const gateCache = new Map<string, React.FC<any>>();

export const withPlanGate = <P extends object>(
  Component: React.ComponentType<P>,
  featureCode: string,
  gateKey?: string,
  options?: { currentCount?: number | null; output?: boolean }
): React.FC<P> => {
  const cacheKey = `${featureCode}::${gateKey ?? ""}::${options?.currentCount ?? ""}::${options?.output ? "output" : ""}`;
  const cached = gateCache.get(cacheKey);

  // ⚠ Keyed on the COMPONENT too — two different pages in one module (race tracking vs race
  // results) share a feature and a key prefix, and handing one the other's wrapper would render
  // the wrong page.
  if (cached && (cached as any).__wrapped === Component) {
    return cached as React.FC<P>;
  }

  const Gated: React.FC<P> = (props) => {
    const { checkFeature } = useFeatureGate();
    const { isInitializing, user } = useApp();
    /**
     * ⚠⚠ WHAT THE ACCOUNT HAS ALREADY BUILT (ROADMAP A2 item 1, owner ruling 2026-09-25). A host who
     * lapses must still SEE the budget they made — MONETIZATION §3.3.1 says what was collected STAYS,
     * and a page they cannot open is not "kept". This route cannot count rows (see the note on
     * `currentCount`), so the server answers it, and only when a lock is about to render.
     * ⚠ It is NOT an entitlement: it unlocks LOOKING, never adding. The Add is refused in-page by the
     * feature gate each of these pages already carries.
     */
    // ⚠ Event pages are `/events/:eventId/...`, so the event half is already wired: when
    // `resolveEventGrant` starts answering (A9.4), an event-upgraded page unlocks with no change
    // here. Account pages simply have no `eventId` and fall through to the account plan.
    const { eventId } = useParams();
    // #142 — the verdict is the EVENT account's in its workspace; the usage read answers for the caller's OWN account
    // only, so on an event worked as CREW it is the wrong account's data and is not consulted: the lock stands.
    const { eventId: workspaceEventId, isCrewMode } = useCrewAccess();
    const crewOnThisEvent = !!eventId && eventId === workspaceEventId && isCrewMode;
    const { resolved, hasData, ensureLoaded } = useFeatureUsage(crewOnThisEvent ? null : user?.account?.id ?? null);

    const verdict = checkFeature(featureCode, {
      eventId: eventId ?? null,
      currentCount: options?.currentCount ?? null,
      // #238 — a print route is an OUTPUT: a draft or a test run is not open to it (`output` on its sitemap entry).
      output: options?.output === true,
    });

    /**
     * ⚠ ASKED ONLY WHEN A LOCK WOULD ACTUALLY RENDER. A paying host never triggers this, and pre-launch
     * nobody can lapse — loading it for everybody at boot would tax every session for a case that does
     * not arise. `useFeatureUsage` caches the answer for the rest of the session.
     * ⚠ In an effect, not in the render body: it sets state, and calling it inline makes the first
     * render of every locked page a state update during render.
     */
    useEffect(() => {
      if (!verdict.allowed && !isInitializing) {
        ensureLoaded();
      }
    }, [verdict.allowed, isInitializing, ensureLoaded]);

    // ⚠ The plan is read from the user record, so the first render of a hard reload has no plan
    // yet. Rendering the lock during that moment would flash an upgrade screen at a paying
    // customer; `checkFeature` fails open for the same reason, and this is the belt to that brace.
    // ⚠ Below the effect (#129): returned above it, the render after initialising called one hook more than the one
    // before, and React threw ("Rendered more hooks than during the previous render").
    if (isInitializing) {
      return null;
    }

    if (verdict.allowed) {
      // ⚠ Our OWN Suspense boundary. The sitemap hands us a `React.lazy` page, and whether the
      // router puts a boundary above this wrapper is the library's business, not ours — so we
      // carry one rather than depend on it.
      return (
        <Suspense fallback={null}>
          <Component {...props} />
        </Suspense>
      );
    }

    /**
     * ⚠⚠ "UNKNOWN" IS NOT "NO". While the answer is in flight we render NOTHING rather than the lock —
     * a host who does have rows would otherwise see a flash of an upgrade screen over their own data,
     * which is the same flicker the `isInitializing` guard above exists to prevent.
     */
    if (!resolved && !crewOnThisEvent) {
      return null;
    }

    /**
     * ⚠⚠ THE FALL-THROUGH. They are not entitled, but they built something here, so they may LOOK at
     * it. Every one of these pages refuses its own Add through the feature gate, and the server
     * refuses the write regardless — this only decides whether the rows are visible.
     */
    if (!crewOnThisEvent && hasData(featureCode)) {
      return (
        <Suspense fallback={null}>
          <Component {...props} />
        </Suspense>
      );
    }

    return (
      <PremiumFeatureGate
        featureCode={featureCode}
        eventId={eventId ?? null}
        currentCount={options?.currentCount ?? null}
        featureDescription={<FeatureGateContent gateKey={gateKey} featureCode={featureCode} />}
      >
        {null}
      </PremiumFeatureGate>
    );
  };

  Gated.displayName = `withPlanGate(${Component.displayName || Component.name || "Page"})`;
  (Gated as any).__wrapped = Component;
  // ⚠ FORWARD THE PREFETCH. `siteMap.ts` puts a `preload()` on every lazy page so the nav can start
  // its chunk on hover (see `lazyNamed`). Wrapping the page here would hide it, and the gated
  // modules — Budget, Rentals, Inventory, Travel, Tickets — are exactly the heavy ones whose first
  // visit flashes hardest. `preloadRoute` reads it off whatever the sitemap holds, which is this.
  (Gated as any).preload = (Component as any).preload;
  gateCache.set(cacheKey, Gated as React.FC<any>);

  return Gated;
};
